import type {NestedDataPathSegment, SingleLayoutData} from '@wix/document-services-types'
// eslint-disable-next-line wix-editor/no-internal-import,import/no-extraneous-dependencies
import {ContentResizeDirection, type ContainerLayout} from '@wix/ambassador-devcenter-v1-component-type-data/types'
import _ from 'lodash'

export const getManifestItems = (meta: any) => meta?.data?.items || meta?.dataItem?.data?.items || {}

const DEFAULT_HORIZONTAL_WIDTH = 100
const DEFAULT_VERTICAL_HEIGHT = 100

interface RowConfig {
    type: string
    min: {type: string; value?: number | null | undefined}
    max: {type: string; value?: number}
}

interface ColumnConfig {
    type: string
    min: {type: string; value: number | null | undefined}
    max: {type: string; value: number}
}

const createLayout = (rows: RowConfig[], columns: ColumnConfig[]): Partial<SingleLayoutData> => ({
    containerLayout: {
        type: 'GridContainerLayout',
        rows,
        columns
    },
    spx: {
        refWidth: 1280,
        resolverType: 'scale'
    },
    componentLayout: {},
    // @ts-ignore
    itemLayout: {},
    type: 'SingleLayoutData'
})

const getDefaultRow = (): RowConfig => ({
    type: 'MinMaxSize',
    min: {
        type: 'maxContent'
    },
    max: {
        type: 'percentage',
        value: 100
    }
})

const getVerticalRow = (height?: number | null): RowConfig => ({
    type: 'MinMaxSize',
    min: {
        type: 'spx',
        value: height ?? DEFAULT_VERTICAL_HEIGHT
    },
    max: {type: 'auto'}
})

const getDefaultColumn = (): ColumnConfig => ({
    type: 'MinMaxSize',
    min: {
        type: 'px',
        value: 0
    },
    max: {
        type: 'fr',
        value: 1
    }
})

const getHorizontalColumn = (width?: number | null): ColumnConfig => ({
    type: 'MinMaxSize',
    min: {
        type: 'px',
        value: width ?? DEFAULT_HORIZONTAL_WIDTH
    },
    max: {
        type: 'fr',
        value: 1
    }
})

export const systemContainerDefaultLayout = (layoutConfig?: ContainerLayout): Partial<SingleLayoutData> | undefined => {
    if (!layoutConfig) {
        return createLayout([getDefaultRow()], [getDefaultColumn()])
    }

    const {containerResizeDirection, width, height} = layoutConfig

    switch (containerResizeDirection) {
        case ContentResizeDirection.horizontal:
            return createLayout([getDefaultRow()], [getHorizontalColumn(width)])
        case ContentResizeDirection.vertical:
            return createLayout([getVerticalRow(height)], [getDefaultColumn()])
        case ContentResizeDirection.horizontalAndVertical:
            return createLayout([getVerticalRow(height)], [getHorizontalColumn(width)])
        default:
            return createLayout([getDefaultRow()], [getDefaultColumn()])
    }
}

export const getDataValueByNestedPath = (
    nestedDataPath: NestedDataPathSegment[] | undefined,
    componentData: Record<string, any> | undefined,
    valueKey: string
): any => {
    if (!componentData) {
        return undefined
    }

    if (!nestedDataPath?.length) {
        return componentData?.[valueKey]
    }

    let currentNodes: any[] = [componentData]

    for (const {key, _id, type} of nestedDataPath) {
        if (type === 'array') {
            currentNodes = _.flatMap(currentNodes, node => {
                const arrayValue = node?.[key]
                if (!_.isArray(arrayValue)) {
                    return []
                }
                if (_id) {
                    return arrayValue.filter((item: any) => item?._id === _id)
                }
                return arrayValue
            })
            continue
        }

        currentNodes = currentNodes
            .map(node => node?.[key])
            .filter(node => {
                if (!node) {
                    return false
                }
                if (!_id) {
                    return true
                }
                return node?._id === _id
            })
    }

    return currentNodes[0]?.[valueKey]
}
